/**
 * LegalRulesCard — paramétrage des règles juridiques par tenant.
 * SaaS-first : chaque tenant ajuste ses seuils selon ses statuts.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Scale, Save, RotateCcw, Loader2, Info } from "lucide-react";
import { toast } from "sonner";

const MAJORITE_LABELS = {
  moitie: "Majorité simple (>50%)",
  tiers: "Un tiers",
  deux_tiers: "Deux tiers",
  unanimite: "Unanimité",
  statuts: "Selon statuts",
};

export default function LegalRulesCard() {
  const [rules, setRules] = useState(null);
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [edits, setEdits] = useState({});

  const load = async () => {
    try {
      const r = await api.get("/legal/rules");
      setRules(r.data.rules);
      setMeta({ countries: r.data.supported_countries,
                  formes: r.data.supported_formes_by_country });
      setEdits({});
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/legal/rules", edits);
      toast.success("Règles juridiques mises à jour");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Réinitialiser aux valeurs par défaut pour votre pays/forme ?")) return;
    setResetting(true);
    try {
      await api.post("/legal/rules/reset");
      toast.success("Règles réinitialisées");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setResetting(false); }
  };

  if (!rules) return null;

  const val = (key) => edits[key] ?? rules[key];
  const set = (key, v) => setEdits({ ...edits, [key]: v });
  const hasChanges = Object.keys(edits).length > 0;
  const hasOverrides = rules._meta?.has_overrides;

  return (
    <Card className="border-slate-300" data-testid="legal-rules-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Scale className="w-4 h-4 text-slate-600" />
            Paramètres juridiques
            <Badge variant="outline" className="text-[10px]">
              {rules._meta?.pays || "FR"} · {rules._meta?.forme_juridique || "SARL"}
            </Badge>
            {hasOverrides && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Personnalisé</Badge>}
          </CardTitle>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={reset} disabled={resetting || !hasOverrides} data-testid="rules-reset">
              {resetting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
              Reset
            </Button>
            <Button size="sm" onClick={save} disabled={saving || !hasChanges} data-testid="rules-save">
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
              Enregistrer
            </Button>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
          <Info className="w-3 h-3" />
          Ajustez selon vos statuts. Les valeurs par défaut viennent du pays et de la forme juridique.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Délai convocation AGO (jours)</Label>
            <Input type="number" value={val("delai_convocation_ago_jours") ?? ""}
                    onChange={(e) => set("delai_convocation_ago_jours", parseInt(e.target.value) || 0)}
                    data-testid="rules-delai-ago" />
          </div>
          <div>
            <Label className="text-xs">Délai convocation AGE (jours)</Label>
            <Input type="number" value={val("delai_convocation_age_jours") ?? ""}
                    onChange={(e) => set("delai_convocation_age_jours", parseInt(e.target.value) || 0)}
                    data-testid="rules-delai-age" />
          </div>
          <div>
            <Label className="text-xs">Quorum AGO (%)</Label>
            <Input type="number" step="0.01" value={val("quorum_ago_pct") ?? ""}
                    onChange={(e) => set("quorum_ago_pct", parseFloat(e.target.value) || 0)} />
          </div>
          <div>
            <Label className="text-xs">Quorum AGE (%)</Label>
            <Input type="number" step="0.01" value={val("quorum_age_pct") ?? ""}
                    onChange={(e) => set("quorum_age_pct", parseFloat(e.target.value) || 0)} />
          </div>
          <div>
            <Label className="text-xs">Majorité AGO</Label>
            <Select value={val("majorite_ago") || "moitie"} onValueChange={(v) => set("majorite_ago", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(MAJORITE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Majorité AGE</Label>
            <Select value={val("majorite_age") || "deux_tiers"} onValueChange={(v) => set("majorite_age", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(MAJORITE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Taux réserve légale (%)</Label>
            <Input type="number" step="0.01" value={val("reserve_legale_taux") ?? ""}
                    onChange={(e) => set("reserve_legale_taux", parseFloat(e.target.value) || 0)} />
          </div>
          <div>
            <Label className="text-xs">Plafond réserve (% capital)</Label>
            <Input type="number" step="0.01" value={val("reserve_legale_plafond_capital") ?? ""}
                    onChange={(e) => set("reserve_legale_plafond_capital", parseFloat(e.target.value) || 0)} />
          </div>
          <div>
            <Label className="text-xs">Délai dépôt greffe post-AGO (jours)</Label>
            <Input type="number" value={val("delai_depot_greffe_apres_ago_jours") ?? ""}
                    onChange={(e) => set("delai_depot_greffe_apres_ago_jours", parseInt(e.target.value) || 0)} />
          </div>
          <div>
            <Label className="text-xs">Délai tenue AGO post-clôture (mois)</Label>
            <Input type="number" value={val("delai_tenue_ago_apres_cloture_mois") ?? ""}
                    onChange={(e) => set("delai_tenue_ago_apres_cloture_mois", parseInt(e.target.value) || 0)} />
          </div>
        </div>
        {rules.articles_reference && (
          <div className="p-2 bg-slate-50 rounded text-[11px] text-slate-600">
            <strong>Articles de référence :</strong> {Object.entries(rules.articles_reference).map(([k, v]) => `${k}: ${v}`).join(" · ")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
